import React, { useState } from 'react';
import { ArrowDown, ArrowUp, MoreVertical } from 'lucide-react';
import IconButton from '../../ui/IconButton';
import Tooltip from '../../ui/Tooltip';
import { cx } from '../../ui/classNames';
import {
    watchlistGridClasses as c,
    watchlistGridClassNames as cn,
} from './watchlistGridStyles';

const TITLE_CLASS_NAME = cx(c.headerTitle, cn.headerTitle);

function SeparatorIcon() {
    return (
        <svg
            className={cx(c.columnSeparatorIcon, cn.columnSeparatorIcon)}
            viewBox="0 0 24 24"
            focusable="false"
            aria-hidden="true"
        >
            <rect width="1" height="24" x="11.5" rx="0.5" />
        </svg>
    );
}

// A text header shows its full label in a tooltip only when it is truncated.
// Header functions that add controls next to their label use it too, so
// every label has the same type style.
export function HeaderTitle({ label }) {
    const [overflowTitle, setOverflowTitle] = useState('');
    return (
        <Tooltip content={overflowTitle}>
            <div
                className={TITLE_CLASS_NAME}
                onMouseOver={(event) => {
                    const element = event.currentTarget;
                    const overflown = element.scrollHeight > element.clientHeight
                        || element.scrollWidth > element.clientWidth;
                    setOverflowTitle(overflown ? label : '');
                }}
            >
                {label}
            </div>
        </Tooltip>
    );
}

const stopEvent = (event) => {
    event.preventDefault();
    event.stopPropagation();
};

function WatchlistGridHeader({
    headerRef,
    columnLayout,
    height,
    sortDirections,
    tabbableField,
    openMenuField,
    menuId,
    getMenuButtonId,
    resizingColumnId,
    onHeaderClick,
    onHeaderKeyDown,
    onHeaderFocus,
    onMenuButtonClick,
    onResizeStart,
    onAutosize,
}) {
    const lastIndex = columnLayout.length - 1;
    // The header box is exactly `height` tall, its bottom line included, so
    // body rows start where the row virtualizer places them; the row and its
    // cells fill what the line leaves.
    return (
        <div
            role="rowgroup"
            className={cx(c.header, cn.header)}
            ref={headerRef}
            style={{ height }}
        >
            <div role="row" aria-rowindex={1} className={cx(c.headerRow, cn.headerRow)}>
                {columnLayout.map((entry, index) => {
                    const { field, column, header, headerAlign } = entry;
                    const { header: headerContent, meta } = column.columnDef;
                    const sortDirection = sortDirections[field] || null;
                    const sortable = column.getCanSort();
                    // The sort direction; unsorted columns preview ascending, their first.
                    const SortIcon = sortDirection === 'desc' ? ArrowDown : ArrowUp;
                    const menuOpen = openMenuField === field;
                    const resizable = column.getCanResize();
                    const ariaSort = sortDirection === 'asc'
                        ? 'ascending'
                        : sortDirection === 'desc' ? 'descending' : 'none';
                    return (
                        <div
                            key={field}
                            role="columnheader"
                            aria-colindex={index + 1}
                            aria-sort={ariaSort}
                            data-field={field}
                            tabIndex={field === tabbableField ? 0 : -1}
                            className={cx(
                                entry.headerClassName,
                                index === 0 && cn.headerCellFirst,
                                sortable && c.headerCellSortable,
                                sortable && cn.headerCellSortable,
                                sortDirection && c.headerCellSorted,
                                index === lastIndex && c.headerCellLast,
                                meta?.headerClassName,
                            )}
                            style={entry.style}
                            onClick={(event) => onHeaderClick(event, field)}
                            onKeyDown={(event) => onHeaderKeyDown(event, field, index)}
                            onFocus={(event) => onHeaderFocus(event, field)}
                        >
                            <div
                                className={cx(c.headerContent, cn.headerContent[headerAlign])}
                                role="presentation"
                            >
                                <div
                                    className={cx(
                                        c.headerTitleContainer,
                                        cn.headerTitleContainer[headerAlign],
                                    )}
                                    role="presentation"
                                >
                                    <div className={cx(c.headerTitleContent, cn.headerTitleContent)}>
                                        {typeof headerContent === 'function'
                                            ? headerContent(header.getContext())
                                            : <HeaderTitle label={headerContent} />}
                                    </div>
                                    {(sortable || sortDirection) && (
                                        // Decorative: the header itself sorts and
                                        // aria-sort announces the direction.
                                        <div
                                            className={cx(c.headerIconContainer, cn.headerIconContainer)}
                                            aria-hidden="true"
                                        >
                                            <IconButton
                                                label="Sort"
                                                title="Sort"
                                                tabIndex={-1}
                                                disabled={!sortable}
                                            >
                                                <SortIcon className={cx(c.sortIcon, cn.headerIcon)} />
                                            </IconButton>
                                        </div>
                                    )}
                                </div>
                                {!meta?.disableColumnMenu && (
                                    // Shown on hover for the pointer (keyboard users
                                    // open the menu with Ctrl/Cmd+Enter on the header),
                                    // so it names the header only while open.
                                    <div
                                        className={cx(
                                            c.headerMenu,
                                            cn.headerMenu[headerAlign],
                                            menuOpen && c.headerMenuOpen,
                                        )}
                                        aria-hidden={menuOpen ? undefined : 'true'}
                                    >
                                        <Tooltip content="Menu" delay={1000}>
                                            <IconButton
                                                id={getMenuButtonId(index)}
                                                label="Menu"
                                                tabIndex={-1}
                                                aria-haspopup="menu"
                                                aria-expanded={menuOpen}
                                                aria-controls={menuOpen ? menuId : undefined}
                                                onClick={(event) => {
                                                    stopEvent(event);
                                                    onMenuButtonClick(field, event.currentTarget);
                                                }}
                                            >
                                                <MoreVertical aria-hidden="true" className={cn.headerIcon} />
                                            </IconButton>
                                        </Tooltip>
                                    </div>
                                )}
                            </div>
                            <div
                                className={cx(
                                    c.columnSeparator,
                                    cn.columnSeparator,
                                    entry.lastPinned ? cn.columnSeparatorOffsetBordered : cn.columnSeparatorOffset,
                                    resizable && c.columnSeparatorResizable,
                                    resizingColumnId === field && c.columnSeparatorResizing,
                                )}
                                style={{ minHeight: height }}
                                onMouseDown={resizable ? (event) => onResizeStart(event, field) : undefined}
                                onTouchStart={resizable ? (event) => onResizeStart(event, field) : undefined}
                                onDoubleClick={resizable ? (event) => onAutosize(event, field) : undefined}
                                onClick={stopEvent}
                            >
                                <SeparatorIcon />
                            </div>
                        </div>
                    );
                })}
                <div role="presentation" className={cx(c.filler, cn.headerFiller)} />
            </div>
        </div>
    );
}

export default React.memo(WatchlistGridHeader);
